import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import { User } from "@/types/user";
import { hashPassword } from "@/utils/auth";

export const savePageContent = async (pageKey: string, content: string) => {
  try {
    console.log(`Saving content for page key: ${pageKey}`);
    
    // Check if content exists first
    const { data: existingContent, error: fetchError } = await supabase
      .from('page_contents')
      .select('id')
      .eq('page_key', pageKey)
      .maybeSingle();
    
    if (fetchError) {
      console.error("Fetch error:", fetchError);
      toast({
        title: "Hata",
        description: "İçerik kontrol edilirken bir sorun oluştu.",
        variant: "destructive",
      });
      return null;
    }
    
    let result;
    if (existingContent) {
      console.log(`Updating existing content with id: ${existingContent.id}`);
      // Update existing content
      result = await supabase
        .from('page_contents')
        .update({ content, updated_at: new Date().toISOString() })
        .eq('id', existingContent.id)
        .select('*');
    } else {
      console.log(`Creating new content for page key: ${pageKey}`);
      // Create new content
      result = await supabase
        .from('page_contents')
        .insert({ page_key: pageKey, content })
        .select('*');
    }
    
    if (result.error) {
      console.error("Save error:", result.error);
      toast({
        title: "Hata",
        description: "İçerik kaydedilirken bir sorun oluştu.",
        variant: "destructive",
      });
      return null;
    }
    
    console.log("Content saved successfully:", result.data?.[0]);
    toast({
      title: "Başarılı",
      description: "İçerik başarıyla kaydedildi.",
    });
    
    return result.data?.[0] || null;
  } catch (error) {
    console.error("Supabase servis hatası:", error);
    toast({
      title: "Hata",
      description: "Beklenmeyen bir hata oluştu.",
      variant: "destructive",
    });
    return null;
  }
};

export const getPageContent = async (pageKey: string) => {
  try {
    console.log("Fetching page content for key:", pageKey);
    const { data, error } = await supabase
      .from('page_contents')
      .select('content')
      .eq('page_key', pageKey)
      .maybeSingle();
    
    if (error) {
      console.error("İçerik getirme hatası:", error);
      return null;
    }
    
    console.log("Page content retrieved:", data?.content || "No content found");
    return data?.content || null;
  } catch (error) {
    console.error("Supabase servis hatası:", error);
    return null;
  }
};

export const saveServiceCategory = async (name: string, icon: string, description: string, image_url: string) => {
  try {
    console.log("Saving service category:", { name, icon, description, image_url });
    
    // Gerekli alanların kontrolü
    if (!name) {
      console.error("Kategori adı zorunludur.");
      return null;
    }
    
    // Sıra numarasını bul
    const { data: categories } = await supabase
      .from('service_categories')
      .select('order_num')
      .order('order_num', { ascending: false })
      .limit(1);
    
    const nextOrder = categories && categories.length > 0 ? (categories[0].order_num || 0) + 1 : 1;
    console.log("Yeni kategori sırası:", nextOrder);
    
    // Ekleme verilerini hazırla
    const insertData: Record<string, any> = {
      order_num: nextOrder
    };
    
    // Sadece tanımlı değerleri ekle
    if (name !== undefined) insertData.name = name;
    if (icon !== undefined) insertData.icon = icon;
    if (description !== undefined) insertData.description = description;
    if (image_url !== undefined) insertData.image_url = image_url;
    
    console.log("Ekleme verileri:", insertData);
    
    const { data, error } = await supabase
      .from('service_categories')
      .insert(insertData)
      .select();
      
    if (error) {
      console.error("Kategori kaydetme hatası:", error);
      return null;
    }
    
    console.log("Kategori başarıyla kaydedildi:", data?.[0]);
    return data?.[0] || null;
  } catch (error) {
    console.error("Supabase servis hatası:", error);
    return null;
  }
};

export const getServiceCategories = async () => {
  try {
    console.log("Fetching service categories");
    const { data, error } = await supabase
      .from('service_categories')
      .select('*')
      .order('order_num', { ascending: true });
      
    if (error) {
      console.error("Kategorileri getirme hatası:", error);
      return [];
    }
    
    console.log("Service categories retrieved:", data?.length || 0);
    return data || [];
  } catch (error) {
    console.error("Supabase servis hatası:", error);
    return [];
  }
};

// Supabase bağlantı testi için fonksiyon
export const testSupabaseConnection = async () => {
  try {
    const { data, error } = await supabase
      .from('service_categories')
      .select('*')
      .limit(1);
    if (error) {
      console.error('Supabase bağlantı testi başarısız:', error);
      return false;
    }
    console.log('Supabase bağlantı testi başarılı:', data);
    return true;
  } catch (err) {
    console.error('Supabase bağlantı testinde hata:', err);
    return false;
  }
};

// Kullanıcı işlemleri için fonksiyonlar
export const findUserByUsername = async (username: string) => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('id, username, password_hash, role')
      .eq('username', username)
      .single();
    
    if (error) {
      console.error("Kullanıcı getirme hatası:", error);
      return null;
    }
    
    if (!data) return null;
    
    return {
      id: data.id,
      username: data.username,
      password: data.password_hash,
      role: data.role
    };
  } catch (error) {
    console.error("Supabase servis hatası:", error);
    return null;
  }
};

export const createUser = async (username: string, password: string, role: string = 'user') => {
  try {
    const hashedPassword = hashPassword(password);
    
    const { data, error } = await supabase
      .from('users')
      .insert({
        username,
        password_hash: hashedPassword,
        role
      })
      .select('id, username, role')
      .single();
    
    if (error) {
      console.error("Kullanıcı oluşturma hatası:", error);
      return null;
    }
    
    return {
      id: data.id,
      username: data.username,
      role: data.role
    };
  } catch (error) {
    console.error("Supabase servis hatası:", error);
    return null;
  }
};

export const updateUserPassword = async (userId: string, newPasswordHash: string) => {
  try {
    const { data, error } = await supabase
      .from('users')
      .update({
        password_hash: newPasswordHash
      })
      .eq('id', userId)
      .select('id')
      .single();
    
    if (error) {
      console.error("Şifre güncelleme hatası:", error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error("Supabase servis hatası:", error);
    return false;
  }
};

// Kullanıcı adı değiştirme fonksiyonu
export const updateUsername = async (userId: string, newUsername: string) => {
  try {
    // Önce yeni kullanıcı adının var olup olmadığını kontrol et
    const { data: existingUser, error: checkError } = await supabase
      .from('users')
      .select('id')
      .eq('username', newUsername)
      .maybeSingle();
    
    if (checkError) {
      console.error("Kullanıcı adı kontrolü hatası:", checkError);
      return { success: false, message: "Kontrol sırasında bir hata oluştu." };
    }
    
    if (existingUser) {
      return { success: false, message: "Bu kullanıcı adı zaten kullanılıyor." };
    }
    
    // Kullanıcı adını güncelle
    const { data, error } = await supabase
      .from('users')
      .update({
        username: newUsername
      })
      .eq('id', userId)
      .select('id, username')
      .single();
    
    if (error) {
      console.error("Kullanıcı adı güncelleme hatası:", error);
      return { success: false, message: "Güncelleme sırasında bir hata oluştu." };
    }
    
    return {
      success: true,
      data: {
        id: data.id,
        username: data.username
      },
      message: "Kullanıcı adı başarıyla güncellendi."
    };
  } catch (error) {
    console.error("Supabase servis hatası:", error);
    return { success: false, message: "Beklenmeyen bir hata oluştu." };
  }
};

export const setupInitialAdmin = async () => {
  try {
    // Admin kullanıcısının var olup olmadığını kontrol et
    const { data, error } = await supabase
      .from('users')
      .select('id')
      .eq('username', 'admin')
      .single();
    
    if (error && error.code !== 'PGRST116') {  // PGRST116: Line not found
      console.error("Admin kontrolü hatası:", error);
      return false;
    }
    
    // Admin yoksa oluştur
    if (!data) {
      const adminUser = await createUser('admin', 'admin123', 'admin');
      if (!adminUser) {
        console.error("Varsayılan admin oluşturulamadı");
        return false;
      }
      console.log("Varsayılan admin oluşturuldu");
    } else {
      console.log("Admin kullanıcısı zaten var");
    }
    
    return true;
  } catch (error) {
    console.error("Başlangıç ayarları hatası:", error);
    return false;
  }
};

// Yeni fonksiyon: ID'ye göre kategori getirme
export const getServiceCategoryById = async (id: string) => {
  try {
    console.log("Fetching service category by ID:", id);
    
    if (!id) {
      console.error("Geçersiz kategori ID'si");
      return null;
    }
    
    const { data, error } = await supabase
      .from('service_categories')
      .select('*')
      .eq('id', id)
      .single();
      
    if (error) {
      console.error("Kategori getirme hatası:", error);
      return null;
    }
    
    console.log("Kategori başarıyla getirildi:", data);
    return data;
  } catch (error) {
    console.error("Supabase servis hatası:", error);
    return null;
  }
};

// Yeni fonksiyon: Kategori güncelleme
export const updateServiceCategory = async (
  id: string, 
  name: string, 
  icon: string, 
  description: string, 
  image_url: string
) => {
  try {
    console.log("Updating service category:", { id, name, icon, description, image_url });
    
    if (!id) {
      console.error("Geçersiz kategori ID'si");
      return null;
    }
    
    // Güncelleme verilerini hazırla
    const updateData: Record<string, any> = {};
    
    // Sadece tanımlı değerleri ekle
    if (name !== undefined) updateData.name = name;
    if (icon !== undefined) updateData.icon = icon;
    if (description !== undefined) updateData.description = description;
    if (image_url !== undefined) updateData.image_url = image_url;
    
    console.log("Güncelleme verileri:", updateData);
    
    const { data, error } = await supabase
      .from('service_categories')
      .update(updateData)
      .eq('id', id)
      .select();
      
    if (error) {
      console.error("Kategori güncelleme hatası:", error);
      return null;
    }
    
    console.log("Kategori başarıyla güncellendi:", data?.[0]);
    return data?.[0] || null;
  } catch (error) {
    console.error("Supabase servis hatası:", error);
    return null;
  }
};
